import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportWidgetConfigurableArea, ReportWidgetTemplateConfig } from '../../report-widget-template.model';
import { ReportWidgetTemplateRenderApi } from '../../report-widget-template-render-api.model';

@Component({
  selector: 'app-report-widget-top-one-spotlight-template',
  templateUrl: './top-one-spotlight-template.component.html',
  styleUrls: ['./top-one-spotlight-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0 min-w-0' },
  standalone: false
})
export class ReportWidgetTopOneSpotlightTemplateComponent {
  @Input() rows: Record<string, any>[] = [];
  @Input() config: ReportWidgetTemplateConfig | null = null;
  @Input() api!: ReportWidgetTemplateRenderApi;

  readonly leaderIcon: IconProp = ['fal', 'crown'];

  get leader(): Record<string, any> | null {
    return this.rows?.[0] || null;
  }

  get followers(): Record<string, any>[] {
    return Array.isArray(this.rows) ? this.rows.slice(1) : [];
  }

  get titleArea(): ReportWidgetConfigurableArea | null {
    const areas = this.visibleAreas;
    return areas.find((area) => this.areaRole(area) === 'title') || areas[0] || null;
  }

  get primaryArea(): ReportWidgetConfigurableArea | null {
    const areas = this.visibleAreas;
    const titleKey = this.titleArea?.key || '';
    return areas.find((area) => area.key !== titleKey && this.areaRole(area) === 'primaryValue')
      || areas.find((area) => area.key !== titleKey)
      || null;
  }

  get detailAreas(): ReportWidgetConfigurableArea[] {
    const titleKey = this.titleArea?.key || '';
    const primaryKey = this.primaryArea?.key || '';
    return this.visibleAreas.filter((area) => area.key !== titleKey && area.key !== primaryKey);
  }

  rowStyle(row: Record<string, any> | null): Record<string, string> {
    return this.api?.rowStyle(row) || {};
  }

  trackArea = (index: number, item: ReportWidgetConfigurableArea): string => {
    return item?.key || `${index}`;
  };

  trackRow(index: number, row: Record<string, any>): string {
    const id = row?.['id'] || row?.['Id'] || row?.['trackItemId'] || row?.['TrackItemId'];
    return id ? `${id}` : `${index}`;
  }

  trackDecoration = (index: number, item: any): string => {
    return this.api?.trackDecoration(index, item) || `${index}`;
  };

  private get visibleAreas(): ReportWidgetConfigurableArea[] {
    const areas = Array.isArray(this.config?.configurableAreas) && this.config?.configurableAreas?.length
      ? this.config.configurableAreas
      : [
        { key: 'title', role: 'title' },
        { key: 'primaryValue', role: 'primaryValue' }
      ];

    return areas.filter((area) => {
      const role = this.areaRole(area);
      return !!area?.key && role !== 'color' && this.api?.hasSlotBinding(area.key);
    });
  }

  private areaRole(area: ReportWidgetConfigurableArea | null | undefined): string {
    return `${area?.role || area?.key || ''}`.trim();
  }
}
